<template>
  <div class="flex">
    <button
      class="h-[34px] w-8 rounded rounded-r-none border border-r-0 border-neutral-30 bg-white text-neutral-50"
      data-testid="button-minus"
      :aria-label="labelDecrease"
      @click="changeQuantity(-1)"
    >
      <LazyIconFielmannMinus class="size-5" />
    </button>
    <input
      v-model="quantity"
      type="text"
      class="input-element h-[34px] w-16 text-center"
      disabled
      aria-disabled
    />
    <button
      class="h-[34px] w-8 rounded rounded-l-none border border-l-0 border-neutral-30 bg-white text-neutral-50"
      data-testid="button-plus"
      :aria-label="labelIncrease"
      @click="changeQuantity(1)"
    >
      <LazyIconFielmannPlus class="size-5" />
    </button>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  value: {
    type: Number as PropType<number>,
    required: true,
  },
  maxQuantity: {
    type: Number as PropType<number>,
    required: true,
  },
  minQuantity: {
    type: Number as PropType<number>,
    default: () => 1,
  },
  disabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  labelIncrease: {
    type: String as PropType<string>,
    default: '+',
  },
  labelDecrease: {
    type: String as PropType<string>,
    default: '–',
  },
})

const emit = defineEmits(['input'])

const quantity = ref(props.value)
const scope = effectScope()
onScopeDispose(() => scope.stop())

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(
    () => props.value,
    (value) => {
      quantity.value = value
    },
    { immediate: true },
  )

  watch(
    () => props.maxQuantity,
    (newValue) => {
      if (quantity.value > newValue) {
        quantity.value = newValue
      }
    },
  )

  watch(
    () => props.minQuantity,
    (newValue) => {
      if (quantity.value < newValue) {
        quantity.value = newValue
      }
    },
  )
})

const changeQuantity = (value: number) => {
  quantity.value = Math.min(
    props.maxQuantity,
    Math.max(props.minQuantity, quantity.value + value),
  )

  emit('input', quantity.value)
}
</script>

<style lang="scss" scoped>
.input-element {
  -webkit-text-fill-color: black; /* Override iOS / Android font color change */
  -webkit-opacity: 1; /* Override iOS opacity change affecting text & background color */
  opacity: 1;
  color: black; /* Override IE font color change */
}
</style>
